import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useAccess, useAccountId } from "../Auth/auth-functions";
import { AzureADRoles } from "../Auth/auth-roles";
import { api } from "./api";
import { ListUserDto, MutableUserDto, UserDto } from "../models/user.models";

export const useGetUsersQuery = () => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['users', accountId],
		queryFn: async () => {
			let res = await api.get<ListUserDto[]>(`/users`);
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess
	});
}

export const useGetUserByIdQuery = (userId: string | null) => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['user', accountId, userId],
		queryFn: async () => {
			let res = await api.get<UserDto>(`/users/${userId}`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && userId !== "" && userId !== "new" && userId !== null
	});
}

export const useCreateUserMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<UserDto>, Error, MutableUserDto>({
		mutationFn: (model) => {
			return api.post('/users', model);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users', accountId] });
		}
	});
}

export const useUpdateUserMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<UserDto>, Error, MutableUserDto>({
		mutationFn: (model) => {
			return api.put(`/users/${model.UserId}`, model);
		},
		onSuccess: (_data, model) => {
			queryClient.invalidateQueries({ queryKey: ['user', accountId, model.UserId] });
			queryClient.invalidateQueries({ queryKey: ['users', accountId] });

		}
	});
}

export const useDeleteUserMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<unknown, Error, UserDto>({
		mutationFn: (user) => {
			return api.delete(`/users/${user.UserId}`);
		},
		onSuccess: (_data, user) => {
			queryClient.invalidateQueries({ queryKey: ['user', accountId, user.UserId] });
			queryClient.invalidateQueries({ queryKey: ['users', accountId] });
		}
	});
}

export const useRestoreUserMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<unknown, Error, UserDto>({
		mutationFn: (user) => {
			return api.post(`/users/${user.UserId}/restore`, user);
		},
		onSuccess: (_data, user) => {
			queryClient.invalidateQueries({ queryKey: ['user', accountId, user.UserId] });
			queryClient.invalidateQueries({ queryKey: ['users', accountId] });
		}
	});
}